import React, { useState, useMemo } from "react";
import { StyleSheet, View, ScrollView, TouchableOpacity } from "react-native";
import { router } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import ThemedScreen from "@/components/ThemedScreen";
import { ThemedText } from "@/components/ThemedText";
import { ThemedPanel } from "@/components/ThemedPanel";
import { ThemedButton } from "@/components/ThemedButton";
import { ThemedToggle } from "@/components/ThemedToggle";
import { ComponentRefTag } from "@/components/design/ComponentRefTag";
import { ThemedActionHoverPanel, type IBatchAction } from "@/components/ThemedActionHoverPanel";
import { useThemeColor } from "@/hooks/useThemeColor";
import { BrandColors, Spacing, Fonts } from "@/constants/ThemeColours";

interface ISampleItem {
  id: string;
  title: string;
  subtitle: string;
  icon: keyof typeof Ionicons.glyphMap;
  category: string;
}

const SAMPLE_ITEMS: ISampleItem[] = [
  { id: "item-1", title: "Pacific Coast Highway", subtitle: "Archival Drive 1974", icon: "image-outline", category: "Media" },
  { id: "item-2", title: "Summer Solstice Garden", subtitle: "Heirloom Gathering 1982", icon: "book-outline", category: "Memory" },
  { id: "item-3", title: "Monterey Lighthouse Survey", subtitle: "Field Notes 1991", icon: "document-text-outline", category: "Memory" },
  { id: "item-4", title: "Redwood Canopy Sunlight", subtitle: "High-Resolution Scan", icon: "images-outline", category: "Media" },
  { id: "item-5", title: "Maritime Harbor Chart", subtitle: "Survey Map 1898", icon: "map-outline", category: "Media" },
  { id: "item-6", title: "Evelyn Vance", subtitle: "Archival Curator", icon: "person-outline", category: "Contact" },
];

type ActionPreset = "media" | "memories" | "contacts" | "minimal";

/**
 * ThemedActionHoverPanel Designer Showcase Screen.
 * Provides interactive evaluation of the floating contextual batch action toolbar across
 * different action presets, selection counts, inline vs floating layouts, and click handling.
 */
export default function ActionHoverPanelDesignerScreen() {
  const [selectedIds, setSelectedIds] = useState<string[]>(["item-1", "item-2"]);
  const [preset, setPreset] = useState<ActionPreset>("media");
  const [isFloating, setIsFloating] = useState(true);
  const [lastActionResult, setLastActionResult] = useState<string | null>(null);

  const cardBg = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const tintColor = useThemeColor({}, "tint");
  const textMuted = useThemeColor({}, "textMuted");
  const badgeBg = useThemeColor({}, "buttonSecondary");

  const toggleItem = (id: string) => {
    setSelectedIds((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      setLastActionResult(
        prev.includes(id)
          ? `Deselected "${SAMPLE_ITEMS.find((i) => i.id === id)?.title}" (${next.length} remaining)`
          : `Selected "${SAMPLE_ITEMS.find((i) => i.id === id)?.title}" (${next.length} selected)`
      );
      return next;
    });
  };

  const handleSelectAll = () => {
    const allIds = SAMPLE_ITEMS.map((i) => i.id);
    setSelectedIds(allIds);
    setLastActionResult(`Selected all ${allIds.length} items`);
  };

  const handleClearSelection = () => {
    setSelectedIds([]);
    setLastActionResult("Cleared selection (panel dismissed)");
  };

  const actions: IBatchAction[] = useMemo(() => {
    const count = selectedIds.length;
    switch (preset) {
      case "media":
        return [
          {
            id: "add-to-memory",
            label: "Add to Memory",
            icon: "book-outline",
            onPress: () => setLastActionResult(`Triggered "Add to Memory" on ${count} media item(s)`),
          },
          {
            id: "share",
            label: "Share",
            icon: "share-social-outline",
            onPress: () => setLastActionResult(`Triggered "Share" modal for ${count} item(s)`),
          },
          {
            id: "tags",
            label: "Add Tags",
            icon: "pricetag-outline",
            onPress: () => setLastActionResult(`Triggered "Add Tags" for ${count} item(s)`),
          },
          {
            id: "delete",
            label: "Delete",
            icon: "trash-outline",
            destructive: true,
            onPress: () => setLastActionResult(`Triggered destructive "Delete" on ${count} item(s)`),
          },
        ];
      case "memories":
        return [
          {
            id: "share-memory",
            label: "Share Memories",
            icon: "share-social-outline",
            onPress: () => setLastActionResult(`Triggered "Share Memories" for ${count} memory(s)`),
          },
          {
            id: "add-to-group",
            label: "Add to Group",
            icon: "people-outline",
            onPress: () => setLastActionResult(`Triggered "Add to Group" for ${count} memory(s)`),
          },
          {
            id: "export-pdf",
            label: "Export PDF",
            icon: "download-outline",
            onPress: () => setLastActionResult(`Triggered "Export PDF" for ${count} memory(s)`),
          },
          {
            id: "trash-memory",
            label: "Move to Trash",
            icon: "trash-outline",
            destructive: true,
            onPress: () => setLastActionResult(`Triggered "Move to Trash" on ${count} memory(s)`),
          },
        ];
      case "contacts":
        return [
          {
            id: "message-batch",
            label: "Send Message",
            icon: "mail-outline",
            onPress: () => setLastActionResult(`Triggered "Send Message" to ${count} recipient(s)`),
          },
          {
            id: "add-to-group-contact",
            label: "Add to Group",
            icon: "people-outline",
            onPress: () => setLastActionResult(`Triggered "Add to Group" for ${count} contact(s)`),
          },
          {
            id: "remove-contact",
            label: "Remove",
            icon: "close-circle-outline",
            destructive: true,
            onPress: () => setLastActionResult(`Triggered "Remove" on ${count} contact(s)`),
          },
        ];
      case "minimal":
      default:
        return [
          {
            id: "quick-share",
            label: "Share",
            icon: "share-outline",
            onPress: () => setLastActionResult(`Triggered "Share" on ${count} item(s)`),
          },
          {
            id: "quick-delete",
            label: "Delete",
            icon: "trash-outline",
            destructive: true,
            onPress: () => setLastActionResult(`Triggered "Delete" on ${count} item(s)`),
          },
        ];
    }
  }, [preset, selectedIds.length]);

  return (
    <ThemedScreen
      title="Action Hover Panel Designer"
      leftIcon={{
        name: "arrow-back",
        onPress: () => router.navigate("/admin/design" as any),
      }}
    >
      <ScrollView contentContainerStyle={styles.container}>
        {/* ========================================================================= */}
        {/* Overview Header */}
        {/* ========================================================================= */}
        <ThemedPanel padding={14} elevation="low" style={styles.sectionPanel}>
          <View style={styles.sectionHeader}>
            <View style={styles.sectionTitleGroup}>
              <View style={[styles.iconCircle, { backgroundColor: badgeBg }]}>
                <Ionicons name="layers" size={22} color={tintColor} />
              </View>
              <View style={{ flex: 1 }}>
                <ThemedText type="defaultSemiBold" style={styles.sectionTitle}>
                  ThemedActionHoverPanel
                </ThemedText>
                <ThemedText style={[styles.sectionSubtitle, { color: textMuted }]}>
                  Floating contextual action bar rendered when one or more items are selected in a list. Styled in signature Archival Blue Linen with high-contrast Obsidian Ink accents and ThemedToolTip integrations.
                </ThemedText>
              </View>
            </View>
            <ComponentRefTag name="REF: <ThemedActionHoverPanel>" />
          </View>
        </ThemedPanel>

        {/* ========================================================================= */}
        {/* Harness Configuration */}
        {/* ========================================================================= */}
        <ThemedPanel padding={14} elevation="low" style={styles.sectionPanel}>
          <ThemedText type="defaultSemiBold" style={styles.cardHeaderTitle}>
            Harness Configuration
          </ThemedText>

          {/* Action Preset Selector */}
          <ThemedText style={[styles.configLabel, { color: textMuted }]}>
            Action Domain Preset:
          </ThemedText>
          <View style={styles.toggleRow}>
            {(
              [
                { key: "media", label: "Media Batch (4 actions)" },
                { key: "memories", label: "Memories (4 actions)" },
                { key: "contacts", label: "Contacts (3 actions)" },
                { key: "minimal", label: "Minimal (2 actions)" },
              ] as const
            ).map((item) => {
              const active = preset === item.key;
              return (
                <TouchableOpacity
                  key={item.key}
                  style={[
                    styles.toggleOption,
                    { borderColor },
                    active && { backgroundColor: tintColor, borderColor: tintColor },
                  ]}
                  onPress={() => setPreset(item.key)}
                >
                  <ThemedText
                    style={[
                      styles.toggleOptionText,
                      active && { color: "#FFFFFF", fontWeight: "700" },
                    ]}
                  >
                    {item.label}
                  </ThemedText>
                </TouchableOpacity>
              );
            })}
          </View>

          {/* Layout Mode Toggle */}
          <View style={styles.toggleSettingRow}>
            <View style={{ flex: 1 }}>
              <ThemedText style={styles.settingTitle}>Float at Screen Bottom</ThemedText>
              <ThemedText style={[styles.settingSubtitle, { color: textMuted }]}>
                When enabled, floats above tab bar; when disabled, mounts inline inside the specimen box below.
              </ThemedText>
            </View>
            <ThemedToggle value={isFloating} onValueChange={setIsFloating} />
          </View>
        </ThemedPanel>

        {/* ========================================================================= */}
        {/* Live Inspector Panel */}
        {/* ========================================================================= */}
        <ThemedPanel padding={14} elevation="low" style={styles.sectionPanel}>
          <ThemedText type="defaultSemiBold" style={styles.cardHeaderTitle}>
            Live Inspector
          </ThemedText>
          <View style={[styles.inspectorBox, { backgroundColor: cardBg, borderColor }]}>
            <ThemedText style={styles.inspectorLabel}>Selected Count:</ThemedText>
            <ThemedText style={styles.inspectorValue}>
              {selectedIds.length} of {SAMPLE_ITEMS.length} item(s) selected
            </ThemedText>
          </View>
          <View style={[styles.inspectorBox, { backgroundColor: cardBg, borderColor, marginTop: 8 }]}>
            <ThemedText style={styles.inspectorLabel}>Last Action Result:</ThemedText>
            <ThemedText
              style={[
                styles.inspectorValue,
                !lastActionResult && { fontStyle: "italic", opacity: 0.6 },
              ]}
            >
              {lastActionResult || "Tap cards below or trigger panel actions to inspect events."}
            </ThemedText>
          </View>
        </ThemedPanel>

        {/* ========================================================================= */}
        {/* Static Inline Preview (If Not Floating) */}
        {/* ========================================================================= */}
        {!isFloating && (
          <ThemedPanel padding={14} elevation="low" style={styles.sectionPanel}>
            <ThemedText type="defaultSemiBold" style={styles.cardHeaderTitle}>
              Inline Specimen Mount Preview
            </ThemedText>
            <View style={[styles.inlinePreviewBox, { borderColor }]}>
              {selectedIds.length > 0 ? (
                <ThemedActionHoverPanel
                  visible={true}
                  floating={false}
                  selectedCount={selectedIds.length}
                  totalCount={SAMPLE_ITEMS.length}
                  onSelectAll={handleSelectAll}
                  onClearSelection={handleClearSelection}
                  actions={actions}
                />
              ) : (
                <ThemedText style={[styles.emptyPreviewText, { color: textMuted }]}>
                  Select at least one card below to mount the panel preview.
                </ThemedText>
              )}
            </View>
          </ThemedPanel>
        )}

        {/* ========================================================================= */}
        {/* Interactive Selection Grid */}
        {/* ========================================================================= */}
        <ThemedPanel padding={14} elevation="low" style={styles.sectionPanel}>
          <View style={styles.gridHeaderRow}>
            <View>
              <ThemedText type="defaultSemiBold" style={styles.cardHeaderTitle}>
                Interactive Sample Items Grid
              </ThemedText>
              <ThemedText style={[styles.gridSubtitle, { color: textMuted }]}>
                Tap any card to toggle selection and see the hover panel respond dynamically.
              </ThemedText>
            </View>
            <View style={styles.quickButtonsRow}>
              <ThemedButton
                title="Select All"
                variant="secondary"
                onPress={handleSelectAll}
              />
              <ThemedButton
                title="Clear"
                variant="minimal"
                onPress={handleClearSelection}
              />
            </View>
          </View>

          <View style={styles.sampleGrid}>
            {SAMPLE_ITEMS.map((item) => {
              const isSelected = selectedIds.includes(item.id);
              return (
                <TouchableOpacity
                  key={item.id}
                  style={[
                    styles.sampleCard,
                    { backgroundColor: cardBg, borderColor },
                    isSelected && {
                      borderColor: tintColor,
                      borderWidth: 2,
                      backgroundColor: "rgba(0, 122, 255, 0.04)",
                    },
                  ]}
                  onPress={() => toggleItem(item.id)}
                  activeOpacity={0.7}
                >
                  <View style={styles.sampleCardHeader}>
                    <View style={[styles.sampleIconBox, { backgroundColor: badgeBg }]}>
                      <Ionicons name={item.icon} size={18} color={tintColor} />
                    </View>
                    <View
                      style={[
                        styles.checkIndicator,
                        { borderColor },
                        isSelected && { backgroundColor: tintColor, borderColor: tintColor },
                      ]}
                    >
                      {isSelected && <Ionicons name="checkmark" size={12} color="#FFFFFF" />}
                    </View>
                  </View>
                  <ThemedText type="defaultSemiBold" style={styles.sampleCardTitle} numberOfLines={1}>
                    {item.title}
                  </ThemedText>
                  <ThemedText style={[styles.sampleCardSubtitle, { color: textMuted }]} numberOfLines={1}>
                    {item.subtitle}
                  </ThemedText>
                  <View style={styles.categoryPill}>
                    <ThemedText style={styles.categoryText}>{item.category}</ThemedText>
                  </View>
                </TouchableOpacity>
              );
            })}
          </View>
        </ThemedPanel>

        {/* Floating spacer to ensure content is visible above floating panel */}
        {isFloating && <View style={{ height: 100 }} />}
      </ScrollView>

      {/* Floating Panel Rendering */}
      {isFloating && (
        <ThemedActionHoverPanel
          visible={selectedIds.length > 0}
          floating={true}
          selectedCount={selectedIds.length}
          totalCount={SAMPLE_ITEMS.length}
          onSelectAll={handleSelectAll}
          onClearSelection={handleClearSelection}
          actions={actions}
        />
      )}
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 12,
    paddingBottom: 40,
  },
  sectionPanel: {
    marginBottom: 14,
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
  },
  sectionTitleGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    flex: 1,
    marginRight: 8,
  },
  iconCircle: {
    width: 44,
    height: 44,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  sectionTitle: {
    fontSize: 16,
  },
  sectionSubtitle: {
    fontSize: 12,
    marginTop: 4,
    lineHeight: 16,
  },
  cardHeaderTitle: {
    fontSize: 14,
    marginBottom: 8,
  },
  configLabel: {
    fontSize: 12,
    fontWeight: "600",
    marginBottom: 6,
  },
  toggleRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  toggleOption: {
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 6,
    borderWidth: 1,
  },
  toggleOptionText: {
    fontSize: 12,
    fontWeight: "500",
  },
  toggleSettingRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 14,
    paddingTop: 12,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: "rgba(0,0,0,0.08)",
  },
  settingTitle: {
    fontSize: 13,
    fontWeight: "600",
  },
  settingSubtitle: {
    fontSize: 11,
    marginTop: 2,
    lineHeight: 14,
  },
  inspectorBox: {
    borderWidth: 1,
    borderRadius: 6,
    padding: 10,
  },
  inspectorLabel: {
    fontSize: 11,
    fontWeight: "700",
    opacity: 0.6,
    marginBottom: 4,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  inspectorValue: {
    fontSize: 13,
    fontWeight: "600",
  },
  inlinePreviewBox: {
    borderWidth: 1,
    borderRadius: 8,
    borderStyle: "dashed",
    paddingVertical: 18,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(0,0,0,0.02)",
  },
  emptyPreviewText: {
    fontSize: 12,
    fontStyle: "italic",
  },
  gridHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  gridSubtitle: {
    fontSize: 11,
    marginTop: 2,
  },
  quickButtonsRow: {
    flexDirection: "row",
    gap: 6,
  },
  sampleGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
  },
  sampleCard: {
    width: "48%",
    borderRadius: 8,
    borderWidth: 1,
    padding: 10,
  },
  sampleCardHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  sampleIconBox: {
    width: 32,
    height: 32,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  checkIndicator: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 1.5,
    alignItems: "center",
    justifyContent: "center",
  },
  sampleCardTitle: {
    fontSize: 13,
  },
  sampleCardSubtitle: {
    fontSize: 11,
    marginTop: 2,
  },
  categoryPill: {
    alignSelf: "flex-start",
    backgroundColor: "rgba(0,0,0,0.06)",
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    marginTop: 6,
  },
  categoryText: {
    fontSize: 10,
    fontWeight: "600",
    opacity: 0.7,
  },
});
